import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Star, Target, Lock, MapPin, Flag, Mountain, Home, Layers } from "lucide-react";
import { api } from "@/lib/api";
import { RANK_BADGES, JOURNEY_MAP, CREST_EMBLEM } from "@/lib/assets";
import { card, label, tagHi, LEVEL_COLORS } from "@/lib/styles";
import Narrator from "@/components/Narrator";

const ZONE = [
  { name: "The Village", icon: Home, flavor: "Where every legend begins." },
  { name: "Whispering Woods", icon: MapPin, flavor: "The foundations take root." },
  { name: "The Crossroads", icon: Flag, flavor: "Retrieval trails diverge." },
  { name: "Highland Pass", icon: Flag, flavor: "Where systems are forged." },
  { name: "The Summit", icon: Mountain, flavor: "Only masters reach the peak." },
];

function Station({ t, side, recommended }) {
  const dot = (
    <div className="relative shrink-0">
      <div className={`w-10 h-10 rounded-full border-2 flex items-center justify-center transition-all ${t.completed ? "border-gold bg-gold/20 shadow-[0_0_16px_-2px_rgba(217,164,65,0.6)]" : recommended ? "border-gem bg-gem/10" : "border-zinc-600 bg-navy-surface"}`}>
        {t.completed ? <CheckCircle2 size={18} className="text-gold" /> : <span className="gem-diamond scale-75" />}
      </div>
    </div>
  );
  const cardInner = (
    <Link
      to={`/topic/${t.topic_id}`}
      data-testid={`topic-link-${t.topic_id}`}
      className={`group inline-flex max-w-[260px] items-center gap-2 rounded-lg border px-3 py-2 transition-all bg-navy-surface/80 backdrop-blur-sm ${t.completed ? "border-gold/40" : "border-zinc-700 hover:border-gold/50"} hover:-translate-y-0.5 ${side === "left" ? "flex-row-reverse text-right" : ""}`}
    >
      <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border shrink-0 ${LEVEL_COLORS[t.level]}`}>{t.level}</span>
      <span className="text-sm text-zinc-200 group-hover:text-gold transition-colors leading-tight">{t.title}</span>
      {t.chapter_count > 1 && (
        <span className="shrink-0 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-gem-light border border-gem/40 bg-gem/10 rounded px-1.5 py-0.5" title={`${t.chapter_count} lessons, including deep dives`}>
          <Layers size={9} /> {t.chapter_count}
        </span>
      )}
    </Link>
  );

  return (
    <div className="relative grid grid-cols-[1fr_auto_1fr] items-center gap-3 my-3" data-testid={`station-${t.topic_id}`}>
      <div className="flex justify-end">{side === "left" && cardInner}</div>
      {dot}
      <div className="flex justify-start">{side === "right" && cardInner}</div>
    </div>
  );
}

export default function Curriculum() {
  const [data, setData] = useState(null);
  const scrolledRef = useRef(false);

  useEffect(() => { api.get("/curriculum").then((r) => setData(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (data && !scrolledRef.current) {
      scrolledRef.current = true;
      const el = document.getElementById(`zone-${data.recommended_tier ?? 0}`);
      setTimeout(() => el?.scrollIntoView({ behavior: "auto", block: "center" }), 60);
    }
  }, [data]);

  if (!data) return <div className="min-h-[60vh] flex items-center justify-center text-gold font-mono"><span className="cursor-blink">unrolling the map</span></div>;

  const focusLabels = (data.user_focus_areas || []).map((id) => data.focus_areas.find((f) => f.id === id)?.label).filter(Boolean);
  // tiers high->low so the Summit is on top and the Village at the bottom
  const tiersTopDown = [...data.tiers].sort((a, b) => b.tier - a.tier);

  return (
    <div className="min-h-screen relative">
      {/* fixed journey backdrop */}
      <div
        className="fixed inset-0 -z-10"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(10,15,28,0.78), rgba(10,15,28,0.55) 55%, rgba(12,20,14,0.6)), url(${JOURNEY_MAP})`,
          backgroundSize: "cover",
          backgroundPosition: "center top",
          backgroundAttachment: "fixed",
        }}
      />

      {/* header */}
      <div className="max-w-3xl mx-auto px-4 pt-12 pb-2 text-center">
        <span className={label}>your quest map</span>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight">The long climb</h1>
        <p className="text-sm text-zinc-400 mt-2">From the village below to the summit above. Tap any waypoint to study it.</p>
        <div className="mt-4 inline-flex items-center gap-2 text-sm">
          <span className="font-serif font-bold text-gold glow-gold text-lg" data-testid="curriculum-progress">{data.completed_count}/{data.total_count}</span>
          <span className="text-zinc-500">waypoints cleared</span>
        </div>
        {focusLabels.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
            <span className="text-xs text-zinc-500 uppercase tracking-wider flex items-center gap-1"><Target size={12} /> focus:</span>
            {focusLabels.map((l) => <span key={l} className={tagHi}>{l}</span>)}
          </div>
        )}
      </div>

      {/* the trail */}
      <div className="relative max-w-2xl mx-auto px-4 pb-24 pt-6">
        {/* center dashed trail line */}
        <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-0.5 bg-[repeating-linear-gradient(to_bottom,rgba(217,164,65,0.5)_0_8px,transparent_8px_18px)]" />

        {tiersTopDown.map((tier) => {
          const zone = ZONE[tier.tier];
          const Icon = zone.icon;
          const isRec = tier.tier === data.recommended_tier;
          const topicsTopDown = [...tier.topics].sort((a, b) => b.order - a.order);
          return (
            <div key={tier.tier} id={`zone-${tier.tier}`} data-testid={`curriculum-tier-${tier.tier}`}>
              {/* zone banner */}
              <div className="relative flex flex-col items-center text-center my-8">
                <img src={RANK_BADGES[tier.tier]} alt="" className="w-20 h-20 select-none pointer-events-none drop-shadow-[0_6px_18px_rgba(217,164,65,0.3)]" style={{ WebkitMaskImage: "radial-gradient(circle,#000 62%,transparent 74%)", maskImage: "radial-gradient(circle,#000 62%,transparent 74%)" }} />
                <div className="mt-2 flex items-center gap-2">
                  <Icon size={16} className="text-gold" />
                  <h2 className="font-serif font-bold text-2xl text-zinc-100">{zone.name}</h2>
                </div>
                <p className="text-xs text-zinc-400">Rank {tier.tier} · {tier.title}: {zone.flavor}</p>
                <div className="flex flex-wrap justify-center gap-2 mt-2">
                  {isRec && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-gem-light border border-gem/50 bg-gem/10 px-2 py-1 rounded" data-testid="recommended-banner"><Star size={11} /> You are here</span>}
                  {tier.is_focus && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-gold border border-gold/50 bg-gold/10 px-2 py-1 rounded"><Target size={11} /> Your focus</span>}
                </div>
              </div>

              {topicsTopDown.map((t, i) => (
                <Station key={t.topic_id} t={t} side={i % 2 === 0 ? "left" : "right"} recommended={isRec} />
              ))}
            </div>
          );
        })}

        {/* trailhead */}
        <div className="relative flex flex-col items-center text-center mt-8">
          <div className="w-12 h-12 rounded-full border-2 border-gold bg-gold/15 flex items-center justify-center gem-glow"><Home size={20} className="text-gold" /></div>
          <p className="font-serif font-bold text-zinc-100 mt-2">Trailhead</p>
          <p className="text-xs text-zinc-500">Every archmage was once a villager.</p>
        </div>

        {/* Altaris greeting */}
        <div className={`${card} max-w-2xl mx-auto mt-8 px-6 py-6 text-center`} data-testid="altaris-greeting">
          <div className="flex items-center justify-center gap-3">
            <img src={CREST_EMBLEM} alt="" className="w-10 h-10 rounded-full border border-gold/50 select-none pointer-events-none" />
            <div className="text-left">
              <h2 className="font-serif font-bold text-xl text-gold glow-gold">Altaris</h2>
              <p className="text-[11px] uppercase tracking-[0.2em] text-zinc-400">Your guide to the summit</p>
            </div>
          </div>
          <p className="text-sm text-zinc-300 mt-4 leading-relaxed">
            Ah, you've found the map at last. I am Altaris, and I will guide your climb from the village below to the summit above. Study the lore, prove yourself in the trials, and when you're ready, I will grade you myself.
          </p>
          <Narrator src="/media/altaris-intro.mp3" label="Hear Altaris greet you" />
        </div>
      </div>
    </div>
  );
}
